import { apiRequest } from '../lib/api-client';

/**
 * Asking a browser to buzz (Phase 119).
 *
 * Three parties, and it is worth naming them because the failure modes belong to different ones:
 * the **browser** grants permission and mints a subscription, the **push service** (Google's,
 * Mozilla's, Apple's) holds the endpoint, and this **product** stores it and later sends to it.
 * None of them tells the others when something changes, which is why a dead subscription is only
 * discovered on the next send.
 *
 * Permission is never asked for on load. A prompt nobody invited is the one everybody blocks, and a
 * block is permanent — it would take the feature away for good over a decision made in half a
 * second. It is asked when somebody turns the switch on.
 */

export type PushState = 'unsupported' | 'denied' | 'off' | 'on';

export interface PushDevice {
  id: string;
  endpoint: string;
  userAgent: string | null;
  createdAt: string;
  lastSentAt: string | null;
}

export interface PushStatus {
  available: boolean;
  publicKey: string | null;
  devices: PushDevice[];
}

export function getPushStatus(): Promise<PushStatus> {
  return apiRequest('/v1/notifications/push');
}

export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof PushManager !== 'undefined' &&
    typeof Notification !== 'undefined'
  );
}

/** What the switch should show, without asking for anything. */
export async function currentPushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();

  return subscription ? 'on' : 'off';
}

/**
 * Turns it on: register the worker, ask, subscribe, and tell the server.
 *
 * The subscription object goes to the server exactly as the browser produced it. Its `toJSON()` is
 * the shape the API takes, and a client that rearranged the keys would be a client that can get
 * them wrong in a way nothing notices until a push is silently undeliverable.
 */
export async function enablePush(publicKey: string): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';

  const permission = await Notification.requestPermission();

  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const registration = await navigator.serviceWorker.register('/sw.js');

  await navigator.serviceWorker.ready;

  const existing = await registration.pushManager.getSubscription();

  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      // Required by every browser: a push that the page cannot be shown for must still show
      // something, and browsers enforce that rather than trusting the application to.
      userVisibleOnly: true,
      applicationServerKey: decodeKey(publicKey),
    }));

  await apiRequest('/v1/notifications/push', { method: 'POST', body: subscription.toJSON() });

  return 'on';
}

/**
 * Turns it off, in both places.
 *
 * The browser subscription is dropped *and* the server is told. Doing only the first leaves the
 * server sending to an endpoint that will answer 410 — harmless but wasteful — and doing only the
 * second leaves the browser holding a subscription nothing will ever use.
 */
export async function disablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();

  if (subscription) {
    await apiRequest('/v1/notifications/push', {
      method: 'DELETE',
      body: { endpoint: subscription.endpoint },
    }).catch(() => undefined);

    await subscription.unsubscribe();
  }

  return 'off';
}

/**
 * The VAPID public key, base64url as the server sends it, as the bytes `subscribe` wants.
 *
 * The one piece of encoding in this file, and it is here because the Push API takes a `Uint8Array`
 * and every server in the world publishes the key as a string.
 */
function decodeKey(base64url: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);

  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);

  // The buffer rather than the view: the typed-array types in this build are generic over the
  // backing buffer, and `subscribe` wants something that is certainly not shared.
  return bytes.buffer;
}
